import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/shared/Button';
import Alert from '../components/shared/Alert';
import logo from '../assets/logo-laranja.png';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('O email é obrigatório');
      return;
    }

    if (!validateEmail(email)) {
      setError('Por favor, insira um email válido');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.error || 'Ocorreu um erro ao solicitar a redefinição de palavra-passe');
      }
    } catch {
      setError('Ocorreu um erro ao conectar com o servidor. Tente novamente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-surface-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-48 w-48 flex items-center justify-center">
              <img
                src={logo}
                alt="doBeco Padaria"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col items-center" style={{ marginTop: '-1.5rem', marginBottom: '2rem' }}>
              <h2 className="text-center text-3xl font-extrabold text-brand-text-primary">
                Email Enviado!
              </h2>
              <p className="mt-2 text-center text-sm text-brand-text-muted">
                Enviamos instruções para redefinir a sua palavra-passe para o email <strong>{email}</strong>
              </p>
            </div>
          </div>

          <div className="mt-8">
            <Alert
              type="success"
              message="Por favor, verifique a sua caixa de entrada (e a pasta de spam) e siga as instruções no email para redefinir a sua palavra-passe. O link de redefinição expirará em 1 hora por motivos de segurança."
            />

            <div className="mt-6">
              <Link to="/login">
                <Button variant="secondary" className="w-full">
                  Voltar para o Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-surface-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-48 w-48 flex items-center justify-center">
            <img
              src={logo}
              alt="doBeco Padaria"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-col items-center" style={{ marginTop: '-1.5rem', marginBottom: '2rem' }}>
            <h2 className="text-center text-3xl font-extrabold text-brand-text-primary">
              Esqueci a Minha Palavra-Passe
            </h2>
            <p className="mt-2 text-center text-sm text-brand-grey[600]">
              Digite o seu email e enviaremos instruções para redefinir a sua palavra-passe
            </p>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-brand-text-primary">
              Email
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-brand-neutral-300 placeholder:text-brand-text-muted text-brand-text-primary rounded-md focus:outline-none focus:ring-brand-primary-500 focus:border-brand-primary-500 sm:text-sm bg-white"
                placeholder="seu@email.com"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Erro */}
          {error && (
            <Alert
              type="error"
              message={error}
            />
          )}

          {/* Botão de envio */}
          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              loading={isLoading}
              className="w-full"
            >
              {isLoading ? 'Enviando...' : 'Enviar Instruções'}
            </Button>
          </div>
        </form>

        {/* Links auxiliares */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/login" className="font-medium text-brand-primary-500 hover:text-brand-primary-400">
                Voltar para o Login
              </Link>
            </div>
          </div>
        </div>

        {/* Informações de segurança */}
        <div className="mt-8">
          <div className="bg-brand-surface-100 border border-brand-neutral-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-brand-text-primary mb-2">
              🔒 Segurança da sua conta
            </h3>
            <ul className="text-sm text-brand-grey[600] space-y-1">
              <li>• O link de redefinição expira em 1 hora</li>
              <li>• Apenas o último link enviado é válido</li>
              <li>• Se não receber o email, verifique a pasta de spam</li>
              <li>• Em caso de dúvidas, contacte o suporte</li>
            </ul>
          </div>
        </div>

        <div className="text-center text-xs text-brand-text-muted">
          doBeco Padaria - Sistema de Controlo de Manutenção
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;